'use client';

import React, { useEffect, useState } from 'react';
import ProductCard from './ProductCard';
import { createClient } from '@/lib/supabase/client';
import { useSiteContent, useSectionVisible } from '@/lib/content/SiteContentContext';
import { restoreScrollIfPending } from '@/lib/scrollRestoration';

interface Product {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
  alt: string;
  scentType: string;
  gender?: string;
  displayPrice?: number;
  displayCurrency?: string;
}

const FeaturedProducts: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const title = useSiteContent('featured_title', 'Featured Collection');
  const subtitle = useSiteContent('featured_subtitle', 'Handpicked luxury fragrances that define elegance and sophistication');
  const visible = useSectionVisible('featured_products');

  useEffect(() => {
    async function load() {
      const supabase = createClient();
      const userCountry = typeof window !== 'undefined'
        ? (localStorage.getItem('detected_country_name') || 'United Arab Emirates')
        : 'United Arab Emirates';

      const [{ data }, { data: cpData }] = await Promise.all([
        supabase
          .from('products')
          .select('id, name, brand, price, image_url, image_alt, scent_type, gender')
          .eq('is_active', true)
          .eq('is_featured', true)
          .limit(6),
        supabase
          .from('product_country_prices')
          .select('product_id, price, currency_code')
          .eq('country_name', userCountry),
      ]);

      const cpMap: Record<string, { price: number; currency_code: string }> = {};
      if (cpData) {
        for (const cp of cpData as { product_id: string; price: number; currency_code: string }[]) {
          const key = String(cp.product_id);
          if (!cpMap[key] || cp.price < cpMap[key].price) {
            cpMap[key] = { price: cp.price, currency_code: cp.currency_code };
          }
        }
      }

      if (data) {
        setFeaturedProducts(
          data.map((p) => {
            const cp = cpMap[String(p.id)];
            return {
              id: p.id,
              name: p.name,
              brand: p.brand || '',
              price: p.price,
              image: p.image_url || '',
              alt: p.image_alt || p.name,
              scentType: p.scent_type || '',
              gender: p.gender || '',
              displayPrice: cp?.price,
              displayCurrency: cp?.currency_code,
            };
          })
        );
      }
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    restoreScrollIfPending();
  }, [loading]);

  if (!visible) return null;

  if (loading) {
    return (
      <section data-component-id="src/app/home/components/FeaturedProducts.tsx:92:6" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="92" data-component-end-line="104" data-component-file="FeaturedProducts.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22w-full%20bg-background%20py-16%20md%3Apy-24%22%7D" className="w-full bg-background py-16 md:py-24">
        <div data-component-id="src/app/home/components/FeaturedProducts.tsx:93:8" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="93" data-component-end-line="103" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-%5B1440px%5D%20px-4%20md%3Apx-6%20lg%3Apx-8%22%7D" className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8">
          <div data-component-id="src/app/home/components/FeaturedProducts.tsx:94:10" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="94" data-component-end-line="97" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%20text-center%22%7D" className="mb-12 text-center">
            <div data-component-id="src/app/home/components/FeaturedProducts.tsx:95:12" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="95" data-component-end-line="95" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20mb-4%20h-10%20w-72%20animate-pulse%20rounded%20bg-muted%22%7D" className="mx-auto mb-4 h-10 w-72 animate-pulse rounded bg-muted" />
            <div data-component-id="src/app/home/components/FeaturedProducts.tsx:96:12" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="96" data-component-end-line="96" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20h-6%20w-96%20animate-pulse%20rounded%20bg-muted%22%7D" className="mx-auto h-6 w-96 animate-pulse rounded bg-muted" />
          </div>
          <div data-component-id="src/app/home/components/FeaturedProducts.tsx:98:10" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="98" data-component-end-line="102" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20gap-6%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%22%7D" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div data-component-id="src/app/home/components/FeaturedProducts.tsx:100:14" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="100" data-component-end-line="100" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-80%20animate-pulse%20rounded-lg%20bg-muted%22%7D" key={i} className="h-80 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (featuredProducts.length === 0) return null;

  return (
    <section data-component-id="src/app/home/components/FeaturedProducts.tsx:111:4" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="111" data-component-end-line="140" data-component-file="FeaturedProducts.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22w-full%20bg-background%20py-16%20md%3Apy-24%22%7D" className="w-full bg-background py-16 md:py-24">
      <div data-component-id="src/app/home/components/FeaturedProducts.tsx:112:6" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="112" data-component-end-line="139" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-%5B1440px%5D%20px-4%20md%3Apx-6%20lg%3Apx-8%22%7D" className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8">
        <div data-component-id="src/app/home/components/FeaturedProducts.tsx:113:8" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="113" data-component-end-line="120" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%20text-center%22%7D" className="mb-12 text-center">
          <h2 data-component-id="src/app/home/components/FeaturedProducts.tsx:114:10" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="114" data-component-end-line="116" data-component-file="FeaturedProducts.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22mb-4%20font-heading%20text-4xl%20font-bold%20text-text-primary%20md%3Atext-5xl%22%7D" className="mb-4 font-heading text-4xl font-bold text-text-primary md:text-5xl">
            {title}
          </h2>
          <p data-component-id="src/app/home/components/FeaturedProducts.tsx:117:10" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="117" data-component-end-line="119" data-component-file="FeaturedProducts.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mx-auto%20max-w-2xl%20font-body%20text-lg%20text-text-secondary%22%7D" className="mx-auto max-w-2xl font-body text-lg text-text-secondary">
            {subtitle}
          </p>
        </div>

        <div data-component-id="src/app/home/components/FeaturedProducts.tsx:122:8" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="122" data-component-end-line="138" data-component-file="FeaturedProducts.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20gap-6%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%22%7D" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featuredProducts.map((product) =>
            <ProductCard data-component-id="src/app/home/components/FeaturedProducts.tsx:124:12" data-component-path="src/app/home/components/FeaturedProducts.tsx" data-component-line="124" data-component-end-line="136" data-component-file="FeaturedProducts.tsx" data-component-name="ProductCard" data-component-content="%7B%22elementName%22%3A%22ProductCard%22%7D"
              key={product.id}
              id={product.id}
              name={product.name}
              brand={product.brand}
              price={product.price}
              image={product.image}
              alt={product.alt}
              scentType={product.scentType}
              gender={product.gender}
              displayPrice={product.displayPrice}
              displayCurrency={product.displayCurrency}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
